import React, { useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthContext } from '../../context/auth.context'
import { HeaderLogo } from './header-logo'
import { OffcanvasButton, OffcanvasEnd, OffcanvasStart } from '../../shared/component/offcanvas'
import { SidebarRight } from './sidebar-right'
import { SidebarLeft } from './sidebar-left'
import { usePageContext } from '../../context/page.context'
import { useAdminNavigation } from '../../role/hook/admin-navigation'
import { IconBars, IconCaretDown, IconLoginDevice } from '../../shared/icon/icon'
import { AccessPinModal } from './login-device/accept-pin-login'
import { useModalActions } from '../../shared/component/modal-dialog'
import { DisplayDesktop, DisplayMobile } from '../../shared/component/display'
import { TransferHeaderList } from '../../shared/transfer/components/transfer-header-list'
import { NotificationBell } from '../../layout/authorized/org/notifications/notification-bell'
import { SIGN_IN_FULL, SIGN_IN_MENU, useSignInDeviceFit } from './header-fit'
import './header.scss'

// The notification bell is held back (owner 2026-10-28: hidden from the
// header until the owner has reviewed the notifications and their problems).
// The inbox stays at /org/notifications. true shows it again; the header's
// tests (header-bell, header-actions) say which way it is.
export const SHOW_NOTIFICATION_BELL = false

const modalPinConfirmID = 'modal-pin-confirm'
const sidebarLeftTarget = 'sidebar-left-target'
const sidebarRightTarget = 'sidebar-right-target'

// The sign-in journey's own pages (/auth/*, and the app's /open/* sign-in
// pages) don't offer a "Login" link: the page already is the sign-in. The
// IT-admin consent pages under /open/sso-consent aren't part of it and keep
// the link, as does every other public page.
export const isSignInPath = pathname =>
  /^\/(auth|open)(\/|$)/.test(pathname || '') && !/^\/open\/sso-consent(\/|$)/.test(pathname)

export const UnAuthHeader = () => {
  const pageContext = usePageContext()
  const { pathname } = useLocation()
  const showLogin = !isSignInPath(pathname)

  return (
    <div className="border-bottom bg-body" style={{ height: '58px' }}>
      <nav className="navbar navbar-expand p-0 h-100">
        <div className="container-fluid p-0 h-100">
          <div className="navbar-brand mx-3">
            <Link to="/auth/email">
              <img className={`logo ${pageContext.theme.isDark && 'logo-gray'} `} src="/EDXR-Logo.png" alt="header logo" />
            </Link>
          </div>

          <div className="collapse navbar-collapse">
            <ul className="navbar-nav">
              {pageContext.host.isLocalhost &&
                <li className="nav-item">
                  <span className="nav-link">
                    <PageSize show={pageContext.host.isLocalhost} />
                  </span>
                </li>}
            </ul>
          </div>

          <div className="collapse navbar-collapse edxr-pointer">
            <ul className="navbar-nav ms-auto">
              {showLogin &&
                <li className="nav-item d-none d-sm-block">
                  <span className="nav-link fs-6 mt-1">
                    <Link to="/auth/email">Login</Link>
                  </span>
                </li>}
            </ul>
          </div>
        </div>
      </nav>
    </div>
  )
}

export const SIGN_IN_DEVICE_TEXT = 'Sign in another device'

// "Sign in another device" in the header, where the row has room for it
// (header-fit.js): with its words when they fit too, else the icon with the
// words as tooltip and name. Without room it is in the account menu instead,
// never in both places.
const SignInDeviceButton = React.forwardRef(({ target, withText }, ref) => {
  const modalActions = useModalActions(target)

  return (
    <button ref={ref} type="button" className="btn btn-light border mx-1" aria-label={SIGN_IN_DEVICE_TEXT} title={SIGN_IN_DEVICE_TEXT} onClick={() => modalActions.show()} data-testid="header-sign-in-device">
      <IconLoginDevice />
      {withText && <span>{SIGN_IN_DEVICE_TEXT}</span>}
    </button>
  )
})

// The two sizes the button can take, laid out off-screen so the fit is
// measured, never guessed. Hidden from everyone: not focusable, not read.
const SignInDeviceMeasure = ({ fullRef, iconRef }) =>
  <span className="header-measure" aria-hidden="true" data-testid="header-measure">
    <button ref={fullRef} type="button" tabIndex={-1} className="btn btn-light border mx-1">
      <IconLoginDevice />
      <span>{SIGN_IN_DEVICE_TEXT}</span>
    </button>
    <button ref={iconRef} type="button" tabIndex={-1} className="btn btn-light border mx-1">
      <IconLoginDevice />
    </button>
  </span>

// The signed-in header (owner report 2026-10-19, a phone showing two bells
// and a cut-off fourth button): one row of buttons at every width, each
// mounted once. Left to right: Transfers (only while an upload or a copy is
// running), the notification inbox's bell, Sign in another device (where
// there is room, header-fit.js) and the account menu (the email from 768 px,
// cut short with an ellipsis; a caret alone on a phone). The row never wraps
// and is never cut off: Sign in another device gives way first, then the
// logo gets smaller (header.scss).
export const AuthHeader = () => {
  const authContext = useAuthContext()
  const adminNavigation = useAdminNavigation()
  const email = authContext.state.jwtClaims.email

  const room = {
    row: useRef(null),
    brand: useRef(null),
    pageSize: useRef(null),
    actions: useRef(null),
    signIn: useRef(null),
    measureFull: useRef(null),
    measureIcon: useRef(null)
  }
  const signInFit = useSignInDeviceFit(room)

  // No admin navigation: no menu button for an empty sidebar.
  const menu = adminNavigation.has &&
    <OffcanvasButton target={sidebarLeftTarget} className="btn border-0 p-0 me-2 header-menu-button" label="Menu" testID="header-menu">
      <IconBars size="lg" />
    </OffcanvasButton>

  return (
    <div className="border-bottom bg-body" style={{ height: '58px' }}>
      <nav className="navbar navbar-expand p-0 h-100">
        <div ref={room.row} className="container-fluid p-0 h-100 flex-nowrap">
          {/* The brand normally sits in the sidebar on wide screens; with no
              admin navigation there is no sidebar, so the header carries it
              at every width (owner retest 2: a bare dark header strip). */}
          <div ref={room.brand} className={`navbar-brand header-brand ms-3 me-2 ${adminNavigation.has ? 'd-xs-block d-lg-none' : 'd-block'}`} data-testid="header-brand">
            <div className="header-brand-row">
              {menu}
              <DisplayDesktop>
                <HeaderLogo />
              </DisplayDesktop>
              <DisplayMobile>
                <HeaderLogo small />
              </DisplayMobile>
            </div>
          </div>

          <HeaderPageSize listRef={room.pageSize} />

          <div className="collapse navbar-collapse edxr-pointer">
            <ul className="navbar-nav ms-auto">
              <li className="nav-item">
                <span ref={room.actions} className="nav-link header-actions" data-testid="header-actions" data-sign-in-device={signInFit}>
                  <TransferHeaderList />
                  {SHOW_NOTIFICATION_BELL && <NotificationBell />}
                  {signInFit !== SIGN_IN_MENU &&
                    <SignInDeviceButton ref={room.signIn} target={modalPinConfirmID} withText={signInFit === SIGN_IN_FULL} />}
                  <OffcanvasButton
                    target={sidebarRightTarget}
                    className="btn btn-light border mx-1 header-account"
                    label={email ? `Account menu, ${email}` : 'Account menu'}
                    testID="header-account">
                    <span className="header-account-email d-none d-md-inline-block text-truncate">{email}</span>
                    <IconCaretDown />
                  </OffcanvasButton>
                </span>
              </li>
            </ul>
          </div>

          <SignInDeviceMeasure fullRef={room.measureFull} iconRef={room.measureIcon} />
        </div>
      </nav>
      {adminNavigation.has &&
        <div className="d-xs-block d-lg-none">
          <OffcanvasSidebarLeft target={sidebarLeftTarget} />
        </div>}
      <OffcanvasSidebarRight target={sidebarRightTarget} signInDeviceTarget={signInFit === SIGN_IN_MENU ? modalPinConfirmID : null} />
      <AccessPinModal target={modalPinConfirmID} />
    </div>
  )
}

// localhost's page-size badge: left out on a phone, where it pushed the
// header's buttons onto a second line, over the breadcrumb (its absence
// there means "EX SMALL")
const HeaderPageSize = ({ listRef }) => {
  const pageContext = usePageContext()

  return (
    <div className="collapse navbar-collapse">
      <ul ref={listRef} className="navbar-nav">
        {pageContext.host.isLocalhost &&
          <li className="nav-item d-none d-sm-block">
            <span className="nav-link">
              <PageSize show={pageContext.host.isLocalhost} />
            </span>
          </li>}
      </ul>
    </div>
  )
}

const OffcanvasSidebarLeft = ({ target }) => {
  return (
    <OffcanvasStart target={target} width="260">
      <SidebarLeft target={target} />
    </OffcanvasStart>
  )
}

const OffcanvasSidebarRight = ({ target, signInDeviceTarget }) => {
  const authContext = useAuthContext()

  return (
    // The signed-in email is in the panel's own accounts section now
    // (account-menu-accounts.js), not a line under the header; 360px so an
    // address keeps to one line there (owner 2026-11-02: no wrapped lines).
    <OffcanvasEnd target={target} header={authContext.state.organizationName} width="360">
      <SidebarRight target={target} signInDeviceTarget={signInDeviceTarget} />
    </OffcanvasEnd>
  )
}

const PageSize = () => {
  return (
    <React.Fragment>
      <span className="edxr-d-xs">EX SMALL</span>
      <span className="edxr-d-sm">SMALL</span>
      <span className="edxr-d-md">MEDIUM</span>
      <span className="edxr-d-lg">LARGE</span>
      <span className="edxr-d-xl">EX LARGE</span>
      <span className="edxr-d-xxl">EX EX LARGE</span>
    </React.Fragment>
  )
}
